import { useEffect, useState } from "react";
import { api, mediaUrl, errText } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Avatar, Loading, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Calendar, MapPin, ExternalLink, Copy, Plus, X, Star, Check, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SL_RE = /^(https?:\/\/)?(maps\.secondlife\.com|slurl\.com)\//i;

export default function Events() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [create, setCreate] = useState(false);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const load = () => api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
  useEffect(() => { load(); }, []);

  const rsvp = async (ev, status) => {
    if (!user) return toast.error("Sign in to RSVP");
    try {
      await api.post(`/events/${ev.id}/rsvp?status=${status}`);
      setEvents((list) => list.map((e) => {
        if (e.id !== ev.id) return e;
        const key = status === "attending" ? "attending" : "interested";
        const arr = new Set(e[key] || []); arr.add(user.id);
        return { ...e, [key]: [...arr] };
      }));
      toast.success(status === "attending" ? "You're going! 🎉" : "Marked interested");
    } catch (e) { toast.error(errText(e)); }
  };
  const copy = (u) => { navigator.clipboard?.writeText(u); toast.success("Destination link copied"); };

  const now = Date.now();
  const list = events || [];
  const upcoming = list.filter((e) => { try { return new Date(e.datetime).getTime() >= now - 36e5; } catch { return true; } });
  const past = list.filter((e) => !upcoming.includes(e));

  return (
    <div className="px-4 md:px-6 py-5" data-testid="events-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div>
          <div className="flex items-center gap-2"><Calendar size={22} className="text-gold" /><h1 className="font-display font-bold text-2xl">Events</h1></div>
          <p className="text-sm text-muted-foreground mt-1">Live shows, parties & happenings across the virtual worlds.</p>
        </div>
        {user && <Button onClick={() => setCreate(true)} className="btn-gold rounded-full shrink-0" data-testid="events-create-btn"><Plus size={16} className="mr-1" />Host Event</Button>}
      </div>
      <div className="text-[11px] text-muted-foreground mb-5">Times shown in your timezone: {tz}</div>

      {!events ? <Loading /> : list.length === 0 ? (
        <Empty icon={Calendar} title="No events yet" hint={user ? "Be the first — host an event for the community." : "Check back soon for upcoming VirtuStars events."} />
      ) : (
        <>
          <EventGrid events={upcoming} user={user} rsvp={rsvp} copy={copy} />
          {past.length > 0 && <>
            <h2 className="font-display font-semibold text-lg mt-8 mb-3 text-muted-foreground">Past events</h2>
            <EventGrid events={past} user={user} rsvp={rsvp} copy={copy} past />
          </>}
        </>
      )}

      {create && <CreateEvent onClose={() => setCreate(false)} onCreated={(e) => { setEvents((l) => [e, ...(l || [])]); setCreate(false); }} />}
    </div>
  );
}

function EventGrid({ events, user, rsvp, copy, past }) {
  if (!events.length) return <Empty icon={Calendar} title="Nothing here yet" />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {events.map((e) => {
        const going = user && (e.attending || []).includes(user.id);
        const interested = user && (e.interested || []).includes(user.id);
        const valid = e.join_url && SL_RE.test(e.join_url);
        let when = e.datetime; try { when = new Date(e.datetime).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { /* keep raw */ }
        return (
          <div key={e.id} className={`surface rounded-2xl overflow-hidden card-hover ${past ? "opacity-70" : ""}`} data-testid={`event-${e.id}`}>
            <div className="h-32 bg-black relative">
              {e.artwork_url && <img src={mediaUrl(e.artwork_url)} alt="" className="w-full h-full object-cover" loading="lazy" />}
              {e.world && <span className="absolute top-2 left-2 text-[10px] font-bold uppercase bg-black/70 text-gold rounded px-1.5 py-0.5">{e.world}</span>}
            </div>
            <div className="p-4">
              <div className="font-display font-semibold text-base leading-tight">{e.title}</div>
              <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><Clock size={12} />{when}</div>
              {e.venue && <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin size={12} />{e.venue}</div>}
              {e.description && <p className="text-xs text-muted-foreground line-clamp-2 mt-2">{e.description}</p>}
              {e.host && <div className="flex items-center gap-1.5 mt-3 text-xs text-muted-foreground"><Avatar src={e.host.avatar_url} name={e.host.display_name} size={18} />Hosted by {e.host.display_name}</div>}

              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {!past && <>
                  <button onClick={() => rsvp(e, "attending")} data-testid={`event-going-${e.id}`}
                    className={`text-xs rounded-full px-3 h-8 font-semibold flex items-center gap-1 ${going ? "btn-blue text-white" : "surface-2 border border-white/10 text-muted-foreground hover:text-white"}`}>
                    {going ? <><Check size={13} />Going</> : "Going"}{(e.attending?.length || 0) > 0 ? ` · ${e.attending.length}` : ""}
                  </button>
                  <button onClick={() => rsvp(e, "interested")} data-testid={`event-interested-${e.id}`}
                    className={`text-xs rounded-full px-3 h-8 font-semibold flex items-center gap-1 ${interested ? "bg-gold/15 text-gold border border-gold/30" : "surface-2 border border-white/10 text-muted-foreground hover:text-white"}`}>
                    <Star size={13} className={interested ? "fill-gold" : ""} />Interested
                  </button>
                </>}
                {e.join_url && (valid
                  ? <a href={e.join_url} target="_blank" rel="noreferrer" data-testid={`event-teleport-${e.id}`} className="text-xs rounded-full px-3 h-8 flex items-center gap-1 btn-gold font-semibold"><ExternalLink size={12} />Teleport</a>
                  : <button onClick={() => copy(e.join_url)} data-testid={`event-copylink-${e.id}`} className="text-xs rounded-full px-3 h-8 flex items-center gap-1 surface-2 border border-white/10"><Copy size={12} />Copy link</button>)}
                {e.ticket_url && <a href={e.ticket_url} target="_blank" rel="noreferrer" className="text-xs rounded-full px-3 h-8 flex items-center gap-1 surface-2 border border-white/10">Tickets</a>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

const WORLDS = ["Second Life", "IMVU", "VRChat", "FiveM", "Roblox", "Sansar", "Other"];

function CreateEvent({ onClose, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", datetime: "", world: "Second Life", venue: "", join_url: "", artwork_url: "", ticket_url: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!f.title.trim() || !f.datetime) return toast.error("Title and date/time are required");
    setBusy(true);
    try {
      const payload = { ...f, datetime: new Date(f.datetime).toISOString() };
      const { data } = await api.post("/events", payload);
      toast.success("Event published 🎉");
      onCreated(data);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const field = "w-full surface-2 border border-white/10 rounded-xl px-3 h-11 text-sm outline-none focus:border-eblue/50";
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="create-event-modal">
      <div className="surface rounded-2xl p-5 w-full max-w-md relative max-h-[90vh] overflow-y-auto hide-scroll" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-white" data-testid="create-event-close"><X size={18} /></button>
        <div className="flex items-center gap-2 mb-4"><Calendar size={18} className="text-gold" /><h3 className="font-display font-bold text-lg">Host an Event</h3></div>
        <div className="space-y-3">
          <input value={f.title} onChange={set("title")} placeholder="Event title" className={field} data-testid="event-field-title" />
          <textarea value={f.description} onChange={set("description")} placeholder="Description (optional)" rows={3} className={field.replace("h-11", "py-2.5")} data-testid="event-field-description" />
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Date & time</label>
            <input type="datetime-local" value={f.datetime} onChange={set("datetime")} className={field} data-testid="event-field-datetime" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={f.world} onChange={set("world")} className={field} data-testid="event-field-world">{WORLDS.map((w) => <option key={w} value={w}>{w}</option>)}</select>
            <input value={f.venue} onChange={set("venue")} placeholder="Venue" className={field} data-testid="event-field-venue" />
          </div>
          <input value={f.artwork_url} onChange={set("artwork_url")} placeholder="Artwork image URL (optional)" className={field} data-testid="event-field-artwork" />
          <input value={f.join_url} onChange={set("join_url")} placeholder="Teleport / join link (optional)" className={field} data-testid="event-field-joinurl" />
          <input value={f.ticket_url} onChange={set("ticket_url")} placeholder="Ticket link (optional)" className={field} data-testid="event-field-ticketurl" />
        </div>
        <Button onClick={submit} disabled={busy} className="btn-gold rounded-full w-full mt-4" data-testid="event-submit-btn">{busy ? <Loader2 size={16} className="animate-spin" /> : "Publish Event"}</Button>
      </div>
    </div>
  );
}
